import React, { useRef, useState, useEffect } from "react";
import { Box, type CSSObject, Flex, Text } from "@chakra-ui/react";
import { Section } from "components/section";
import { useHeaderHeight } from "contexts/header-height";
import { useDemoLinkWithParameters } from "hooks/use-demo-link";
import { pricingTables } from "./data";
import { StickyPlanHeader } from "./sticky-header";
import { TABLE_HEAD_WIDTH } from "./utils";
import { TableGroup } from "./table-group";

const groupLabelStyles: CSSObject = {
  borderLeft: "1px solid",
  borderColor: "base.border",
  fontSize: "lg",
  fontWeight: "semibold",
  textAlign: "center",
  w: "100%",
  pt: 2,
};

export function PricingTables() {
  const { headerHeight } = useHeaderHeight();
  const demoLinkWithParameters = useDemoLinkWithParameters();

  const stickyTableHead = useRef<HTMLDivElement>(null);
  const [tableHeadWidth, setTableHeadWidth] = useState(0);

  useEffect(() => {
    const stickyTableHeadObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTableHeadWidth(entry.contentRect.width);
      }
    });

    if (stickyTableHead.current) {
      stickyTableHeadObserver.observe(stickyTableHead.current);
    }

    return () => {
      stickyTableHeadObserver.disconnect();
    };
  }, [tableHeadWidth]);

  return (
    <Section display={{ base: "none", lg: "flex" }} pos="relative" my={0}>
      <Text as="h2" textStyle="h4" textAlign="center" mb={{ base: 8, lg: 20 }}>
        Compare all features
      </Text>

      <Flex
        pos="sticky"
        top={`${headerHeight}px`}
        borderBottom="1px"
        borderColor="base.border"
        bg="white"
        w="100%"
        zIndex={1}
      >
        <Box ref={stickyTableHead} flex={1} maxW={TABLE_HEAD_WIDTH} />

        <Flex
          flex={1}
          justify="end"
          mr={{ base: 0, lg: "0.5px" }} // Table cell border alignment
        >
          <Flex direction="column">
            <Flex>
              <Text sx={{ ...groupLabelStyles }}>Starter</Text>
            </Flex>
            <Flex justify="end">
              <StickyPlanHeader
                price="$0"
                unit="Free forever"
                ctaText="Get started"
                ctaHref="https://app.hightouch.io/signup"
                borderLeft="1px"
                borderColor="base.border"
              />
              <StickyPlanHeader
                price="$350"
                unit="/month"
                ctaText="Start for free"
                ctaHref="https://app.hightouch.io/signup"
              />
            </Flex>
          </Flex>
          <Flex direction="column">
            <Text sx={{ ...groupLabelStyles }}>Business</Text>
            <StickyPlanHeader
              price="Custom"
              ctaText="Contact sales"
              ctaHref={demoLinkWithParameters}
              borderLeft="1px"
              borderColor="base.border"
            />
          </Flex>
        </Flex>
      </Flex>

      {pricingTables.map((tableGroup, groupIndex) => (
        <TableGroup key={tableGroup.heading} isFirst={groupIndex === 0} headingWidth={tableHeadWidth} {...tableGroup} />
      ))}
    </Section>
  );
}
